import { sanitizeLine, sanitizeText } from "./parse";
import { isThemeId, type ThemeId } from "./themes";

/**
 * L'etat de l'atelier dans le stockage local du navigateur : la liste collee, les etoiles,
 * le theme et le titre. Rien ne part au serveur. Chaque acces est sous try/catch : en
 * navigation privee, ou quand le stockage est plein ou bloque, la page marche sans.
 *
 * L'unicite de l'activation par navigateur n'est pas geree ici : la page appelle
 * `onActivation(count)` a chaque changement du nombre de groupes a partir de dix, et c'est
 * le ticket 4 (`api.activation`) qui retient qu'elle a ete comptee.
 */
export const STATE_KEY = "concerts:atelier";

/** Nombre de groupes qui fait une activation (spec, section 3.3). */
export const ACTIVATION_THRESHOLD = 10;

export const DEFAULT_TITLE = "LE LINE-UP DE MA VIE";
export const MAX_TITLE_LENGTH = 40;
/** Longueur maximale du texte colle (la zone de saisie porte la meme limite). */
export const MAX_TEXT_LENGTH = 30_000;

export interface AtelierState {
  text: string;
  /** Cles (bandKey) des groupes etoiles. */
  starred: string[];
  theme: ThemeId;
  title: string;
}

export const DEFAULT_STATE: AtelierState = {
  text: "",
  starred: [],
  theme: "nuit",
  title: DEFAULT_TITLE,
};

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Coupe un texte trop long a la derniere ligne complete. */
export function clampText(text: string): string {
  if (text.length <= MAX_TEXT_LENGTH) {
    return text;
  }
  const head = text.slice(0, MAX_TEXT_LENGTH);
  const lastBreak = head.lastIndexOf("\n");
  return lastBreak >= 0 ? head.slice(0, lastBreak) : head;
}

/** Nettoie un titre et le borne a MAX_TITLE_LENGTH caracteres (sans couper un emoji). */
export function clampTitle(title: string): string {
  return Array.from(sanitizeLine(title)).slice(0, MAX_TITLE_LENGTH).join("");
}

/** Relit l'etat garde, en ignorant tout champ invalide et en retirant les caracteres casses. */
export function loadState(): AtelierState {
  try {
    const raw = storage()?.getItem(STATE_KEY);
    if (!raw) {
      return { ...DEFAULT_STATE };
    }
    const data: unknown = JSON.parse(raw);
    if (typeof data !== "object" || data === null) {
      return { ...DEFAULT_STATE };
    }
    const record = data as Record<string, unknown>;
    return {
      text:
        typeof record.text === "string" ? sanitizeText(clampText(record.text)) : DEFAULT_STATE.text,
      starred: Array.isArray(record.starred)
        ? record.starred.filter((key): key is string => typeof key === "string")
        : [],
      theme: isThemeId(record.theme) ? record.theme : DEFAULT_STATE.theme,
      title: typeof record.title === "string" ? clampTitle(record.title) : DEFAULT_STATE.title,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

export function saveState(state: AtelierState): void {
  try {
    storage()?.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    // Stockage plein ou refuse : la page continue sans memoire.
  }
}

/** Efface la liste gardee (bouton de secours de la page). */
export function clearState(): void {
  try {
    storage()?.removeItem(STATE_KEY);
  } catch {
    // Rien a effacer quand le stockage est refuse.
  }
}
